/**
 * Employee directory export — the inverse of the bulk employee import.
 *
 * Downloads the whole employee directory as a CSV in the exact column layout
 * the importer reads, so HR can pull the master data out of LMS, edit it, and
 * load it straight back in (or into the legacy HRIS). Large directories come
 * back as a zip of CSVs, each under the importer's 500-rows-per-file cap.
 *
 * Uses raw `fetch` (not the shared `apiRequest` client) because it needs the
 * raw blob/response + Content-Disposition, then streams the file to the browser
 * via a blob + temporary anchor. Throws ApiError on a non-2xx so the UI can
 * show a message instead of saving an error page.
 */
import { ApiError } from './client'

const API_BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/+$/, '')

export async function exportEmployees(): Promise<void> {
  const res = await fetch(`${API_BASE}/api/v1/hr/employees/export`, {
    credentials: 'include',
  })
  if (!res.ok) {
    let message = `Export failed (${res.status})`
    try {
      const body = (await res.json()) as { detail?: string }
      if (body.detail) message = body.detail
    } catch {
      // non-JSON error body — keep the status-based message
    }
    throw new ApiError(message, res.status)
  }

  const blob = await res.blob()
  const filename = parseFilename(res.headers.get('Content-Disposition')) ?? 'lms-employees.csv'
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

function parseFilename(disposition: string | null): string | null {
  if (!disposition) return null
  const m = /filename="?([^"]+)"?/.exec(disposition)
  return m?.[1] ?? null
}
